
// Per-instance widget options popover: custom title, the widget's declared
// filters (entity pickers) and, for charts, the X/Y axis choice. Edits write
// straight into the DashboardWidgetEntry — nothing is persisted until the
// dashboard editor's APPLY, so dismissing the editor still cancels everything.
class DashboardWidgetConfigViewController extends MUIViewController
{
    static newInstance() : DashboardWidgetConfigViewController {
        let vc = new DashboardWidgetConfigViewController("dashboard-widget-config-view");
        vc.initWithResource("layout/reports/DashboardWidgetConfigView.html");
        return vc;
    }

    // set by the editor when presenting
    editor:DashboardEditViewController = null;
    reportDelegate:any = null;   // dashboard VC; receives applyVisibilityOptions

    private widgetTitleLabel:MUILabel = null;
    private presentationRow:MUIView = null;
    private presentationComboBox:MUIComboBox = null;
    private titleRow:MUIView = null;
    private titleTextField:MUITextField = null;
    private filterRows:MUIView[] = [];
    private filterLabels:MUILabel[] = [];
    private filterComboBoxes:MUIComboBox[] = [];
    private xAxisRow:MUIView = null;
    private xAxisComboBox:MUIComboBox = null;
    private yAxisRow:MUIView = null;
    private yAxisComboBox:MUIComboBox = null;
    private columnsRow:MUIView = null;
    private columnsButton:MUIButton = null;

    get preferredContentSize(): MIOSize {
        // pushed inside the editor's navigation popover: keep its size
        return new MIOSize(420, 560);
    }

    viewDidLoad() {
        this.widgetTitleLabel = MUIOutlet(this, "widget-title-lbl", "MUILabel");

        let backButton = MUIOutlet(this, "back-btn", "MUIButton");
        backButton?.setAction(this, function(this:DashboardWidgetConfigViewController){
            if (this.navigationController != null) this.navigationController.popViewController(true);
            else this.dismissViewController(true);
        });

        this.presentationRow = MUIOutlet(this, "presentation-row", "MUIView");
        this.presentationComboBox = MUIOutlet(this, "presentation-cb", "MUIComboBox");
        this.presentationComboBox?.setOnChangeAction(this, function(this:DashboardWidgetConfigViewController, control, value:string){
            if (this._entry == null) return;
            if (value.hasPreffix("chart:")) { this._entry.kind = "chart"; this._entry.chartType = parseInt(value.substring(6)); }
            else this._entry.kind = value;
            // axis / columns rows depend on the kind
            this.updateUI();
        });

        this.columnsRow = MUIOutlet(this, "columns-row", "MUIView");
        this.columnsButton = MUIOutlet(this, "columns-btn", "MUIButton");
        this.columnsButton?.setAction(this, this.showColumnOptions);

        this.titleRow = MUIOutlet(this, "title-row", "MUIView");
        this.titleTextField = MUIOutlet(this, "title-tf", "MUITextField");
        this.titleTextField.setOnChangeText(this, function(this:DashboardWidgetConfigViewController, control, value:string){
            if (this._entry == null) return;
            let title = value?.trim();
            this._entry.customTitle = title != null && title.length > 0 ? title : null;
        });

        // the layout carries two filter slots; widgets use at most that today
        for (let slot = 1; slot <= 2; slot++){
            this.filterRows.push(MUIOutlet(this, "filter" + slot + "-row", "MUIView"));
            this.filterLabels.push(MUIOutlet(this, "filter" + slot + "-lbl", "MUILabel"));
            let cb = MUIOutlet(this, "filter" + slot + "-cb", "MUIComboBox");
            let index = slot - 1;
            cb.setOnChangeAction(this, function(this:DashboardWidgetConfigViewController, control, value:string){
                this.filterChanged(index, value);
            });
            this.filterComboBoxes.push(cb);
        }

        this.xAxisRow = MUIOutlet(this, "x-axis-row", "MUIView");
        this.xAxisComboBox = MUIOutlet(this, "x-axis-cb", "MUIComboBox");
        this.xAxisComboBox.setOnChangeAction(this, function(this:DashboardWidgetConfigViewController, control, value:string){
            if (this._entry != null) this._entry.xKey = value.length > 0 ? value : null;
        });

        this.yAxisRow = MUIOutlet(this, "y-axis-row", "MUIView");
        this.yAxisComboBox = MUIOutlet(this, "y-axis-cb", "MUIComboBox");
        this.yAxisComboBox.setOnChangeAction(this, function(this:DashboardWidgetConfigViewController, control, value:string){
            if (this._entry != null) this._entry.yKey = value.length > 0 ? value : null;
        });

        this.updateUI();
    }

    viewWillAppear(animated?:any): void {
        super.viewWillAppear(animated);
        this.updateUI();
    }

    viewWillDisappear(animated?:any): void {
        super.viewWillDisappear(animated);
        // the cell label shows title + presentation: refresh the list behind
        this.editor?.updateUIFromConfig();
    }

    private _entry:DashboardWidgetEntry = null;
    setEntry(entry:DashboardWidgetEntry){
        this._entry = entry;
        this.updateUI();
    }

    private updateUI(){
        if (this.viewIsLoaded == false || this._entry == null) return;
        let entry = this._entry;

        this.widgetTitleLabel.text = MIOLocalizeString(entry.title, entry.title);

        this.fillPresentationComboBox();

        this.titleTextField.text = entry.customTitle ?? "";
        this.titleTextField.setPlaceholderText(MIOLocalizeString(entry.title, entry.title));

        for (let index = 0; index < this.filterRows.length; index++){
            let filter = entry.supportedFilters[index];
            this.filterRows[index].hidden = filter == null;
            if (filter == null) continue;
            this.filterLabels[index].text = filter["Title"] ?? filter["Key"];
            this.fillFilterComboBox(index, filter);
        }

        this.fillAxisComboBox(this.xAxisRow, this.xAxisComboBox, entry.chartXAxes, entry.xKey);
        this.fillAxisComboBox(this.yAxisRow, this.yAxisComboBox, entry.chartYAxes, entry.yKey);

        // column visibility applies to the table presentation, and only once
        // the instance has rendered (that is what registers its columns)
        if (this.columnsRow != null) {
            this.columnsRow.hidden = entry.kind != "table" || ReportView.optionForKey(this.instanceKey()) == null;
        }
    }

    private instanceKey():string {
        return this._entry.item?.identifier ?? this._entry.id;
    }

    // HEADER, TABLE and the chart types the widget supports, as one dropdown
    private fillPresentationComboBox(){
        if (this.presentationComboBox == null) return;
        let entry = this._entry;

        this.presentationComboBox.removeAllItems();
        let optionCount = 0;
        for (let kind of entry.supportedKinds) {
            if (kind == "chart") {
                for (let type = 0; type < DashboardEditWidgetCell.chartTypeTitles.length; type++){
                    let title = DashboardEditWidgetCell.chartTypeTitles[type];
                    this.presentationComboBox.addItem(MIOLocalizeString(title, title), "chart:" + type);
                    optionCount++;
                }
            }
            else {
                let title = kind.toUpperCase();
                this.presentationComboBox.addItem(MIOLocalizeString(title, title), kind);
                optionCount++;
            }
        }
        this.presentationRow.hidden = optionCount < 2;
        this.presentationComboBox.selectItem(entry.kind == "chart" ? "chart:" + entry.chartType : entry.kind);
    }

    private showColumnOptions(){
        let option = ReportView.optionForKey(this.instanceKey());
        if (option == null) return;

        let vc = ReportColumnVisibilityViewController.newInstance();
        vc.delegate = this.reportDelegate;
        vc.setColumnOptions(DashboardViewController.dashboardReportID, [option]);
        if (this.navigationController != null) this.navigationController.pushViewController(vc, true);
        else AppHelper.sharedInstance().presentPopOverViewController(vc, this.columnsButton, true);
    }

    // ALL (empty value) + one item per entity instance, chosen by identifier
    private fillFilterComboBox(index:number, filter:any){
        let cb = this.filterComboBoxes[index];
        cb.removeAllItems();
        cb.addItem(MIOLocalizeString("ALL", "ALL"), "");
        cb.selectItem(this._entry.filterValues[filter["Key"]] ?? "");

        let entry = this._entry;
        DBHelper.queryObjectsWithCompletion(filter["Entity"], [MIOSortDescriptor.sortDescriptorWithKey("name", true)], MIOPredicate.predicateWithFormat("deletedAt == null"), [], this, function(this:DashboardWidgetConfigViewController, objects){
            if (this._entry != entry) return;
            cb.removeAllItems();
            cb.addItem(MIOLocalizeString("ALL", "ALL"), "");
            for (let object of objects) {
                cb.addItem(object.name, object.identifier);
                // a stored title equal to the selection's name was auto-derived
                // in a previous session: keep treating it as replaceable
                if (object.identifier == entry.filterValues[filter["Key"]] && entry.customTitle == object.name) {
                    entry.autoTitle = object.name;
                }
            }
            cb.selectItem(entry.filterValues[filter["Key"]] ?? "");
        });
    }

    private fillAxisComboBox(row:MUIView, cb:MUIComboBox, axes:any[], selected:string){
        let hasChoices = axes != null && axes.length > 1 && this._entry.kind == "chart";
        row.hidden = !hasChoices;
        if (!hasChoices) return;

        cb.removeAllItems();
        for (let axis of axes) {
            cb.addItem(axis["Title"] ?? axis["Key"], axis["Key"]);
        }
        cb.selectItem(selected ?? axes[0]["Key"]);
    }

    private filterChanged(index:number, value:string){
        if (this._entry == null) return;
        let entry = this._entry;
        let filter = entry.supportedFilters[index];
        if (filter == null) return;

        // Name the instance after the filter selection ("BREAKFAST", "Hotel"...)
        // so the user can tell filtered headers apart. A title the user typed
        // themselves always wins; only null or a previous auto-title is replaced.
        let autoTitleAllowed = entry.customTitle == null || entry.customTitle == entry.autoTitle;

        if (value.length > 0) {
            entry.filterValues[filter["Key"]] = value;
            if (autoTitleAllowed) {
                let name = this.filterComboBoxes[index].getSelectedItemText();
                if (name != null && name.length > 0) {
                    entry.customTitle = name;
                    entry.autoTitle = name;
                    this.titleTextField.text = name;
                }
            }
        }
        else {
            delete entry.filterValues[filter["Key"]];
            if (autoTitleAllowed && entry.autoTitle != null) {
                entry.customTitle = null;
                entry.autoTitle = null;
                this.titleTextField.text = "";
            }
        }
    }
}
